import type React from "react";
import { useEffect, useCallback, useRef, useMemo } from "react";
import { Loader2, Search, X } from "lucide-react";
import { Button } from "@planta-v2/pds-ui/button";
import { i18n } from "@frontend/i18n";
import { useDerivedState } from "@frontend/shared/presentation/hooks/useDerivedState";

const DEBOUNCE_MS = 300;

interface DirectorySearchBarProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onBlur?: (value: string) => void;
  readonly totalCount?: number | undefined;
  readonly isSearching?: boolean;
  readonly onFocusChange?: (isFocused: boolean) => void;
  readonly isCancelShown?: boolean;
}

export function DirectorySearchBar({
  value,
  onChange,
  onBlur,
  totalCount,
  isSearching = false,
  onFocusChange,
  isCancelShown = false,
}: DirectorySearchBarProps): React.JSX.Element {
  const [localValue, setLocalValue] = useDerivedState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return (): void => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        onChange(newValue);
      }, DEBOUNCE_MS);
    },
    [onChange, setLocalValue],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Enter") {
        e.preventDefault();

        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
        }
        onChange(localValue);

        onBlur?.(localValue);
      }
    },
    [onChange, onBlur, localValue],
  );

  // Flushed in the same batch as the focus change, so a query still inside its
  // debounce does not leave the box read as idle and untuck the header mid-tap.
  const handleBlur = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    onChange(localValue);
    onBlur?.(localValue);
    onFocusChange?.(false);
  }, [onChange, onBlur, onFocusChange, localValue]);

  const handleFocus = useCallback((): void => {
    onFocusChange?.(true);
  }, [onFocusChange]);

  const handleClear = useCallback((): void => {
    setLocalValue("");

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    onChange("");

    // Clicking the button blurs the input first, so handleBlur has already
    // written the stale query to the URL — sync the cleared value over it.
    onBlur?.("");

    inputRef.current?.focus();
  }, [onChange, onBlur, setLocalValue]);

  const handleCancel = useCallback((): void => {
    // Blur first: its handler syncs the stale query, which the clear then overwrites.
    inputRef.current?.blur();
    setLocalValue("");

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    onChange("");
    onBlur?.("");
  }, [onChange, onBlur, setLocalValue]);

  const placeholder = useMemo(() => {
    if (totalCount !== undefined) {
      if (totalCount === 1) {
        return i18n.t("user.directory.search.placeholderWithCountOne");
      }
      return i18n.t("user.directory.search.placeholderWithCount", {
        count: totalCount,
      });
    }
    return i18n.t("user.directory.search.placeholder");
  }, [totalCount]);

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-1">
        {isSearching ? (
          <Loader2
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none animate-spin"
            data-testid="directory-search-spinner"
          />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        )}
        <input
          ref={inputRef}
          type="text"
          enterKeyHint="search"
          placeholder={placeholder}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className="w-full h-11 pl-9 pr-10 text-base md:text-sm text-foreground rounded-md transition-all placeholder:text-muted-foreground outline-none border-0 bg-accent focus:ring-[3px] focus:ring-ring/30"
          data-testid="directory-search-input"
        />
        {localValue !== "" && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={i18n.t("user.directory.search.clear")}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-3 opacity-60 transition-opacity hover:opacity-100"
            data-testid="directory-search-clear"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>
      {isCancelShown && (
        <Button
          variant="link"
          className="px-0"
          onClick={handleCancel}
          data-testid="directory-search-cancel"
        >
          {i18n.t("common.cancel")}
        </Button>
      )}
    </div>
  );
}
